import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtIDR, curOf } from "../lib/format";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Activity, Wallet, TrendingUp, Layers, DoorOpen, AlertTriangle } from "lucide-react";

export default function OwnerDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/owner").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#64748B]">Memuat...</div>;

  const chart = d.chart.map((c) => ({ ...c, volume: Number(c.volume), profit: Number(c.profit), date: c.date.slice(5) }));

  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-bold">Owner Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={Activity} label="Transaksi Hari Ini" value={d.today.count} testid="dash-count" />
        <Card icon={Wallet} label="Volume Hari Ini" value={fmtIDR(d.today.volume)} testid="dash-volume" />
        <Card icon={TrendingUp} label="Profit Kotor Hari Ini" value={fmtIDR(d.today.gross_profit)} accent testid="dash-profit" />
        <Card icon={Layers} label="Nilai Inventory" value={fmtIDR(d.inventory_value)} testid="dash-inventory" />
        <Card icon={DoorOpen} label="Shift Aktif" value={d.active_shifts} testid="dash-shifts" />
        <Card icon={AlertTriangle} label="Menunggu Approval" value={d.pending_approvals} warn={d.pending_approvals > 0} testid="dash-pending" />
        <Card icon={TrendingUp} label="Net Profit Hari Ini" value={fmtIDR(d.net_profit_today)} accent testid="dash-net-today" />
        <Card icon={TrendingUp} label="Net Profit Bulan Ini" value={fmtIDR(d.net_profit_month)} accent testid="dash-net-month" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="nnx-card p-4">
          <div className="label mb-4">Daily Transaction Volume (7 hari)</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chart}>
              <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EAB308" stopOpacity={0.5} /><stop offset="100%" stopColor="#EAB308" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222B3D" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
              <Tooltip contentStyle={{ background: "#151B28", border: "1px solid #222B3D", borderRadius: 8 }} formatter={(v) => fmtIDR(v)} />
              <Area type="monotone" dataKey="volume" stroke="#EAB308" fill="url(#gv)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="nnx-card p-4">
          <div className="label mb-4">Daily Profit (7 hari)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222B3D" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `${(v / 1e3).toFixed(0)}rb`} />
              <Tooltip contentStyle={{ background: "#151B28", border: "1px solid #222B3D", borderRadius: 8 }} formatter={(v) => fmtIDR(v)} />
              <Bar dataKey="profit" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="nnx-card p-4">
        <div className="label mb-3">Profit per Currency (Hari Ini)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.keys(d.today.profit_by_currency || {}).length === 0 && <div className="text-sm text-[#64748B]">Belum ada data hari ini.</div>}
          {Object.entries(d.today.profit_by_currency || {}).map(([c, p]) => (
            <div key={c} className="rounded-lg border border-[#222B3D] bg-[#0B0E14] p-3">
              <div className="text-sm font-semibold">{curOf(c).symbol} {c}</div>
              <div className={`mono font-bold ${Number(p) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtIDR(p)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, accent, warn, testid }) {
  return (
    <div className={`nnx-card p-4 ${warn ? "border-amber-500/40" : ""}`} data-testid={testid}>
      <Icon className={`h-4 w-4 mb-2 ${accent ? "text-[#EAB308]" : warn ? "text-amber-400" : "text-[#64748B]"}`} />
      <div className="label">{label}</div>
      <div className={`mono font-bold text-lg mt-1 ${accent ? "text-[#EAB308]" : warn ? "text-amber-400" : "text-white"}`}>{value}</div>
    </div>
  );
}
